// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

/**
 * Multi-Context UI - React Hooks
 *
 * Custom hooks for managing multi-context state and operations.
 */

import {
	type ApiClient,
	contextKey,
	createApiClient,
	FetchError,
	getApiClient,
	setApiToken
} from '@cloudillo/core'
import { apiAtom, authAtom, useApi, useAuth, useIsDesktop, useToast } from '@cloudillo/react'
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'

import { crossContextApps } from '../manifest-registry.js'
import { installHatToken } from '../pwa.js'
import { seedCommunityFromHome } from '../read-position.js'
import { CTX_SECTION_MATCH, ctxBase, feedPath, isContextSegment, switchPath } from '../routes.js'
import {
	activeContextAtom,
	activeContextDisplayAtom,
	communitiesAtom,
	contextColorsAtom,
	contextHatRoleAtom,
	contextIdpEnabledAtom,
	contextIdpEnabledCacheAtom,
	contextRolesAtom,
	contextSwitchingAtom,
	favoriteCommunitiesAtom,
	favoritesAtom,
	lastContextSwitchAtom,
	partnerCommunitiesAtom,
	pinKeyFor,
	recentCommunitiesAtom,
	recentContextsAtom,
	refFromActive,
	sidebarAtom,
	sidebarOpenAtom,
	totalUnreadCountAtom,
	withoutPins
} from './atoms'
import type { ContextColor } from './context-colors.js'
import {
	activeKeyFor,
	effectiveTrust,
	hattedConsent,
	mayUseContextToken,
	withoutKey
} from './trust-gate.js'
import type { ActiveContext, CommunityRef, ContextSwitchEvent } from './types'

// Serialized, and each save sends the latest map, so fast picks can't land out of order
let contextColorsSave: Promise<unknown> = Promise.resolve()

/**
 * Fetch `idp.enabled` for a given context and write it into
 * `contextIdpEnabledAtom`. 404 is normal (the setting is just unset) — fall
 * back to `false`. Shared by `switchContext` (per-context API) and the shell
 * `applyUiSettings` path (home-tenant API).
 *
 * The atom takes three values and the failure branch must distinguish them (see
 * `contextIdpEnabledAtom`): 404/403 means the answer really is "off", a transient error means
 * NOTHING was learned and writes `'unknown'`. The RETURN contract is unchanged — `false` for an
 * authoritative answer, `undefined` for "skip the cache write and re-try next time".
 */
export async function loadIdpEnabled(
	client: ApiClient,
	idTag: string,
	setContextIdpEnabled: (
		updater: (prev: Record<string, boolean | 'unknown'>) => Record<string, boolean | 'unknown'>
	) => void
): Promise<boolean | undefined> {
	try {
		const setting = await client.settings.get('idp.enabled')
		const value = setting.value === true
		setContextIdpEnabled((prev) => ({ ...prev, [idTag]: value }))
		return value
	} catch (err) {
		const expected =
			err instanceof FetchError && (err.httpStatus === 404 || err.httpStatus === 403)
		if (!expected) {
			console.warn('Failed to read idp.enabled for context:', err)
		}
		setContextIdpEnabled((prev) => ({ ...prev, [idTag]: expected ? false : 'unknown' }))
		return expected ? false : undefined
	}
}

/**
 * Menu/app ids backed by tenant-owned resources the server guards with `require_leader`. In a
 * community context a non-leader member gets 403 on every request, so hide them entirely rather
 * than render an app that can only fail.
 */
export const LEADER_ONLY_APPS = new Set(['contacts', 'calendar'])

/**
 * Whether the user acts as a leader in `activeContext`.
 *
 * The backend gates tenant-owned resources (address books, calendars, API keys, passkey management,
 * push subscriptions) behind `require_leader`, so anything reached through a community proxy token
 * needs this check first.
 *
 * The own-context short-circuit is required: `getTokenFor` returns `{ token, roles: [] }` for your
 * own idTag, so `activeContext.roles` is legitimately empty at home even though the session token
 * carries `leader`.
 */
export function isContextLeader(
	activeContext: ActiveContext | null | undefined,
	authIdTag: string | undefined
): boolean {
	if (!activeContext) return true
	if (authIdTag && activeContext.idTag === authIdTag) return true
	return (activeContext.roles ?? []).includes('leader')
}

/**
 * Community admin pages (`/@ctx/settings/<page>`) a moderator may open; every other page is
 * leader-only.
 */
const MODERATOR_ADMIN_PAGES: readonly string[] = ['rooms']

/**
 * Whether the user may administer `activeContext` — enter its settings area at all (no `page`),
 * or open one admin page. Leaders may do everything; moderators only `MODERATOR_ADMIN_PAGES`.
 */
export function canAdminContext(
	activeContext: ActiveContext | null | undefined,
	authIdTag: string | undefined,
	page?: string
): boolean {
	if (isContextLeader(activeContext, authIdTag)) return true
	if (!activeContext?.roles.includes('moderator')) return false
	return page === undefined || MODERATOR_ADMIN_PAGES.includes(page)
}

/**
 * Our roles on `idTag`, read under the key its token is registered at: the hatted `B|A` key
 * while a hat is worn there (the rule of `activeKeyFor`, but subscribed).
 */
export function useContextRolesFor(idTag: string | undefined): string[] | undefined {
	const active = useAtomValue(activeContextAtom)
	const roles = useAtomValue(contextRolesAtom)
	if (!idTag) return undefined
	return roles.get(contextKey(idTag, active?.idTag === idTag ? active.hat?.idTag : undefined))
}

/** Context-dependent visibility shared by the sidebar tools and the omnibox. */
export function contextToolAllowed(
	id: string,
	activeContext: ActiveContext | null | undefined,
	authIdTag: string | undefined,
	idpEnabled: Record<string, boolean | 'unknown'>
): boolean {
	switch (id) {
		// A community's settings are its administration area.
		case 'settings':
			return activeContext?.type !== 'community' || canAdminContext(activeContext, authIdTag)
		// `=== true` on purpose: the atom is three-state, and neither 'unknown' (a transient
		// lookup failure) nor a missing entry may offer the IdP page. See contextIdpEnabledAtom.
		case 'idp':
			return !!activeContext && idpEnabled[activeContext.idTag] === true
		default:
			return true
	}
}

/** What `getTokenFor` resolves to: the proxy token and the roles it reports, or a refusal. */
type ProxyTokenResult = { token: string; roles: string[]; role?: string } | null

/** Bumped per `setActiveContext` call; a switch that lost the race drops its result. */
let switchSeq = 0
/** Context key of the latest `setActiveContext` call. */
let switchKey: string | undefined

/**
 * Hook for managing API context and multi-context operations
 *
 * Provides methods to switch contexts, get tokens, and manage API clients
 * for different communities.
 *
 * @example
 * ```typescript
 * const {
 *   activeContext,
 *   setActiveContext,
 *   getTokenFor,
 *   getClientFor
 * } = useApiContext()
 *
 * // Switch to a community
 * await setActiveContext('alice.community')
 *
 * // Get API client for specific context
 * const aliceApi = getClientFor('alice.community')
 * ```
 */
export function useApiContext() {
	const store = useStore()
	const activeContext = useAtomValue(activeContextAtom)
	const setActiveContextState = useSetAtom(activeContextAtom)
	const setContextIdpEnabled = useSetAtom(contextIdpEnabledAtom)
	const setContextIdpEnabledCache = useSetAtom(contextIdpEnabledCacheAtom)
	const setContextRoles = useSetAtom(contextRolesAtom)
	const setContextHatRole = useSetAtom(contextHatRoleAtom)
	const [isLoading, setIsLoading] = React.useState(false)
	const [error, setError] = React.useState<Error | undefined>()

	// In-flight proxy-token fetches, keyed by `contextKey` (target idTag, or `B|A` hatted). The registry only holds a token once
	// it has LANDED, so without this, concurrent first-time callers (DetailsPanel and the
	// ShareDialog it opens both mount useFileOwnerScope for the same file) each issue their own
	// network request.
	const inFlightTokensRef = React.useRef(new Map<string, Promise<ProxyTokenResult>>())

	/**
	 * Get API client for specific context
	 * @param idTag - ID tag of the context
	 * @param opts - Options: token (an explicit scoped token, never cached), auth
	 *   ('required' | 'preferred' | 'none'), explicit
	 *   - 'required' (default): return null if no token available
	 *   - 'preferred': use token if available, create unauthenticated client if not
	 *   - 'none': always create an unauthenticated client
	 *
	 * Reaching for a *cached* foreign token runs through the same trust gate as
	 * `getTokenFor` (see `effectiveTrust`): one explicit action seeds the registry,
	 * and without the gate every later passive read — a title prefetch, a thumbnail
	 * — would keep sending the identified token to that node. Pass
	 * `{ explicit: true }` for user-initiated actions; the home idTag is never gated.
	 *
	 * Cached clients come from the process-wide registry, which owns their tokens
	 * (including `useContextTokenRenewal`'s proactive renewals). Explicit and
	 * anonymous clients stay uncached: a scoped/ref token is not interchangeable
	 * with the context token for the same idTag, and anonymity is guaranteed *by*
	 * not caching. So the uncached fallbacks ('preferred' without a token, and
	 * 'none') return a NEW client on every call — a caller holding the result across
	 * renders, especially in a dep array, must memoize it, keying on
	 * `contextRolesAtom` so the memo releases the anonymous client once a real token
	 * lands.
	 */
	const getClientFor = React.useCallback(
		(
			idTag: string,
			opts?: {
				token?: string
				auth?: 'required' | 'preferred' | 'none'
				explicit?: boolean
			}
		): ApiClient | null => {
			// `|` would alias a hatted registry key (`idTag|hat`).
			if (idTag.includes('|')) return null
			const authMode = opts?.auth ?? 'required'
			const token = opts?.token

			if (token) return createApiClient({ idTag, authToken: token })
			if (authMode === 'none') return createApiClient({ idTag })

			const ownIdTag = store.get(authAtom)?.idTag
			if (mayUseContextToken(store, idTag, { ownIdTag, explicit: opts?.explicit })) {
				return getApiClient(activeKeyFor(store, idTag))
			}

			if (authMode === 'required') {
				console.warn(`No token available for context: ${idTag}`)
				return null
			}
			// 'preferred': unauthenticated client, uncached so it can't shadow the
			// registry entry once a token does land.
			return createApiClient({ idTag })
		},
		[store]
	)

	/**
	 * Get proxy token for specific context.
	 *
	 * By default this is a *passive* read and runs through the profile-trust gate
	 * (`effectiveTrust`):
	 *   - `'always'` / session `'S'` / the active context → fetch (or return
	 *     cached) proxy token.
	 *   - anything else ('never', 'X', or no decision) → return null; the caller
	 *     falls back to anonymous and the profile page's `TrustBanner` derives
	 *     its visibility from effective trust being `null`, so no extra
	 *     signalling is required here.
	 *
	 * The gate runs uniformly on both cache hit and cache miss — a cached token
	 * from an earlier explicit action must not leak identity on later passive
	 * reads once the user's effective trust is no longer a positive consent.
	 *
	 * Pass `{ explicit: true }` for user-initiated actions (follow, comment, message,
	 * etc.). Explicit calls bypass the gate entirely — the action is the user's
	 * consent — and unconditionally fetch/return the proxy token.
	 *
	 * `{ hat: A }` fetches (or returns the cached) token for `idTag` worn under
	 * community A's hat: cached, deduped and role-mapped under `contextKey(idTag, A)`,
	 * never the bare entry. The result's `role` is the capped role that hat grants.
	 *
	 * @returns Object with token and roles, or `null` if anonymous was chosen.
	 */
	const getTokenFor = React.useCallback(
		async (
			idTag: string,
			opts?: { explicit?: boolean; hat?: string }
		): Promise<ProxyTokenResult> => {
			const explicit = opts?.explicit === true
			const hat = opts?.hat || undefined
			const key = contextKey(idTag, hat)
			const auth = store.get(authAtom)

			// User's own context uses primary token
			if (!hat && idTag === auth?.idTag) {
				const tok = auth?.token
				return tok ? { token: tok, roles: [] } : null
			}

			// The home client from the registry — same object `useApi()` hands
			// out, carrying the current home token.
			const ownIdTag = auth?.idTag
			if (!ownIdTag) return null
			const api = getApiClient(ownIdTag)

			// Trust gate: passive reads require positive consent. Anything else
			// (including "ask") stays anonymous. Same evaluation `getClientFor`
			// applies to an already-cached token.
			// A hatted token is only used passively while that exact hat is worn.
			if (
				!explicit &&
				(hat ? !hattedConsent(store, idTag, hat) : effectiveTrust(store, idTag) === 'none')
			)
				return null

			// Cache hit: the registry holds the token and reaps it at `exp`, so a
			// non-null read is by definition still live. Roles ride alongside.
			const cachedToken = getApiClient(key).getAuthToken()
			if (cachedToken) {
				const roles = store.get(contextRolesAtom).get(key) ?? []
				const role = hat ? store.get(contextHatRoleAtom).get(key) : undefined
				return { token: cachedToken, roles, role }
			}

			// Join a fetch already running for this idTag rather than issuing a second one. Both
			// short-circuits above stay ABOVE this: the own-context branch never touches the
			// network, and a passive read refused by the trust gate must not ride in on an explicit
			// caller's request.
			const inFlight = inFlightTokensRef.current.get(key)
			if (inFlight) return inFlight

			const pending = (async function fetchProxyToken(): Promise<ProxyTokenResult> {
				// Fetch new proxy token for the target idTag (wearing `hat` if given)
				const result = await api.auth.getProxyToken(idTag, hat ? { hat } : undefined)
				const roles = result.roles || []

				// The registry owns the token (and derives its expiry from `exp`);
				// the atom carries the roles and the re-render signal.
				setApiToken(key, result.token)
				// Mirror a replaced worn-hat token into the worker, as `renewOne` does
				if (hat && hattedConsent(store, idTag, hat))
					installHatToken(idTag, result.token, hat)
				if (hat) {
					setContextHatRole((prev) => new Map(prev).set(key, result.role ?? ''))
				}
				setContextRoles((prev) => {
					const next = new Map(prev)
					next.set(key, roles)
					return next
				})

				return { token: result.token, roles, role: result.role }
			})()
			// Evicted in `finally`, so a rejection rejects for every awaiter and the next call
			// retries instead of replaying the failure.
			inFlightTokensRef.current.set(key, pending)

			try {
				return await pending
			} catch (err) {
				console.error(`Failed to get proxy token for ${key}:`, err)
				throw err
			} finally {
				inFlightTokensRef.current.delete(key)
			}
		},
		[store, setContextRoles, setContextHatRole]
	)

	/**
	 * Switch to different context
	 * Fetches token if needed and updates active context.
	 *
	 * `ui.*` settings are per-user and are loaded once at boot from the home
	 * tenant — they must not be re-fetched on context switch. `idp.enabled` is
	 * a per-tenant capability and is fetched per context, but cached for a
	 * short staleness window so re-entering a known context skips the call.
	 *
	 * Idempotent: a same-target call (or a same-target call already in flight)
	 * with the URL-as-source-of-truth model returns early. The target is
	 * `(idTag, hat)`: switching the hat on the same idTag is a real switch.
	 *
	 * `hat` enters `idTag` wearing that community's hat (hatted proxy token under
	 * `contextKey(idTag, hat)`); a refused handshake throws like any other.
	 */
	const setActiveContext = React.useCallback(
		async (
			idTag: string,
			opts: { forceRefresh?: boolean; hat?: string } = {}
		): Promise<boolean> => {
			const auth = store.get(authAtom)
			if (!auth?.idTag) {
				throw new Error('Cannot switch context without auth')
			}
			const ownIdTag = auth.idTag
			const hat = opts.hat || undefined

			const current = store.get(activeContextAtom)
			if (current?.idTag === idTag && current.hat?.idTag === hat && !opts.forceRefresh) {
				return true
			}

			const seq = ++switchSeq
			switchKey = contextKey(idTag, hat)
			setIsLoading(true)
			setError(undefined)

			try {
				// Explicit user action: bypass the passive-read gate.
				const tokenResult = await getTokenFor(idTag, { explicit: true, hat })
				if (!tokenResult) {
					throw new Error(`Failed to get token for context: ${idTag}`)
				}
				// A newer switch started while this one awaited its token: it owns the
				// active context now. Silent — a throw would mark this idTag as failed.
				if (seq !== switchSeq) {
					// The hatted token it minted is unusable unless that hat is worn now: drop it
					const key = contextKey(idTag, hat)
					if (hat && key !== switchKey && !hattedConsent(store, idTag, hat)) {
						setApiToken(key, undefined)
						setContextRoles((prev) => withoutKey(prev, key))
						setContextHatRole((prev) => withoutKey(prev, key))
					}
					return false
				}

				// `getTokenFor` has just installed the token on this context key's
				// registry client, so the cached one is already correct.
				const contextApi = getApiClient(contextKey(idTag, hat))

				// Create context with roles from proxy token response.
				// Resolve display data the way the sidebar does so the object
				// carries a real name/avatar for consumers.
				const isMe = idTag === ownIdTag
				const communities = store.get(communitiesAtom)
				const community = isMe ? undefined : communities.find((c) => c.idTag === idTag)
				const hatCommunity = hat ? communities.find((c) => c.idTag === hat) : undefined

				const newContext: ActiveContext = {
					idTag,
					type: isMe ? 'me' : 'community',
					name: isMe ? (auth.name ?? idTag) : (community?.name ?? idTag),
					profilePic: isMe ? auth.profilePic : community?.profilePic,
					hat: hat
						? {
								idTag: hat,
								name: hatCommunity?.name,
								profilePic: hatCommunity?.profilePic,
								role: tokenResult.role ?? ''
							}
						: undefined,
					roles: tokenResult.roles,
					permissions: [],
					metadata: {}
				}

				// Update active context
				setActiveContextState(newContext)

				// idp.enabled is a tenant capability — load per context, but
				// cached. On cache hit within the staleness window, seed
				// contextIdpEnabledAtom from cache and skip the network call.
				const STALE_MS = 5 * 60 * 1000
				const cached = store.get(contextIdpEnabledCacheAtom).get(idTag)
				if (cached && Date.now() - cached.fetchedAt < STALE_MS && !opts.forceRefresh) {
					setContextIdpEnabled((prev) => ({ ...prev, [idTag]: cached.value }))
				} else {
					const value = await loadIdpEnabled(contextApi, idTag, setContextIdpEnabled)
					// Mirror the freshly-written value into the cache so
					// re-entry skips the network call. Skip the cache write
					// on a transient failure (value === undefined) so we
					// don't lock the stale `false` for the next 5 minutes.
					if (value !== undefined) {
						setContextIdpEnabledCache((prev) => {
							const next = new Map(prev)
							next.set(idTag, { value, fetchedAt: Date.now() })
							return next
						})
					}
				}
				return true
			} catch (err) {
				setError(err as Error)
				console.error('Failed to switch context:', err)
				throw err
			} finally {
				if (seq === switchSeq) setIsLoading(false)
			}
		},
		[
			store,
			getTokenFor,
			setActiveContextState,
			setContextIdpEnabled,
			setContextIdpEnabledCache,
			setContextRoles,
			setContextHatRole
		]
	)

	return {
		activeContext,
		setActiveContext,
		getTokenFor,
		getClientFor,
		isLoading,
		error
	}
}

/**
 * Hook for managing communities list
 *
 * Provides access to communities, pins, and methods to manage them. Pins come in three views:
 * `pinnedEntries` (one per key, strip order), `pinnedKeys` (the raw context keys) and
 * `pinnedCommunities` (one per community).
 *
 * @example
 * ```typescript
 * const {
 *   communities,
 *   pinnedEntries,
 *   pinnedKeys,
 *   pinnedCommunities,
 *   recent,
 *   toggleFavorite,
 *   unpinCommunity,
 *   togglePinCommunity
 * } = useCommunitiesList()
 * ```
 */
/**
 * Communities whose mirror we've already fired a best-effort refresh-on-view for
 * this session. Module-level so the dedup survives across the multiple
 * `useCommunitiesList()` instances mounted at once (sidebar, profile pages, …)
 * and across `loadCommunities()` re-runs.
 */
const refreshAttemptedIdTags = new Set<string>()
/** Identity that owns the current `refreshAttemptedIdTags` contents. */
let refreshAttemptedOwner: string | undefined

export function useCommunitiesList() {
	const { api } = useApi()
	const [auth] = useAuth()
	const [communities, setCommunities] = useAtom(communitiesAtom)
	const [favorites, setFavorites] = useAtom(favoritesAtom)
	const [favoriteCommunities] = useAtom(favoriteCommunitiesAtom)
	const [recentCommunities] = useAtom(recentCommunitiesAtom)
	const [totalUnread] = useAtom(totalUnreadCountAtom)
	const store = useStore()
	const [isLoading, setIsLoading] = React.useState(false)
	const [error, setError] = React.useState<Error | undefined>()

	// Reset the once-per-session refresh-on-view dedup only when the owning
	// identity actually changes (logout + login as a different account). Guarded
	// by `refreshAttemptedOwner` so the several `useCommunitiesList()` instances
	// mounted at once don't wipe the dedup on every mount.
	React.useEffect(() => {
		const owner = auth?.idTag
		if (owner && owner !== refreshAttemptedOwner) {
			refreshAttemptedOwner = owner
			refreshAttemptedIdTags.clear()
		}
	}, [auth?.idTag])

	/**
	 * Save pinned communities to backend
	 */
	const savePinnedCommunities = React.useCallback(
		async (newFavorites: string[]) => {
			if (!api) return
			try {
				await api.settings.update('ui.pinned_communities', { value: newFavorites })
			} catch (err) {
				console.error('Failed to save pinned communities:', err)
			}
		},
		[api]
	)

	/** Write the context-color map to the atom and the backend */
	const saveContextColors = React.useCallback(
		(next: Record<string, string>) => {
			store.set(contextColorsAtom, next)
			if (!api) return
			contextColorsSave = contextColorsSave.then(() =>
				api.settings
					.update('ui.context_colors', { value: store.get(contextColorsAtom) })
					.catch((err) => {
						console.error('Failed to save context colors:', err)
					})
			)
		},
		[api, store]
	)

	/** Color context `key` (home's idTag or a pin key); `undefined` resets it to the default */
	const setContextColor = React.useCallback(
		(key: string, color: ContextColor | undefined) => {
			const { [key]: _, ...rest } = store.get(contextColorsAtom)
			saveContextColors(color ? { ...rest, [key]: color } : rest)
		},
		[store, saveContextColors]
	)

	/** Drop the colors of unpinned keys, so the map does not collect orphans */
	const pruneContextColors = React.useCallback(
		(keys: string[]) => {
			const prev = store.get(contextColorsAtom)
			if (!keys.some((k) => Object.hasOwn(prev, k))) return
			saveContextColors(
				Object.fromEntries(Object.entries(prev).filter(([k]) => !keys.includes(k)))
			)
		},
		[store, saveContextColors]
	)

	/**
	 * Load communities from backend (called on app init)
	 */
	const loadCommunities = React.useCallback(async () => {
		if (!api) return

		setIsLoading(true)
		setError(undefined)

		try {
			// Fetch community profiles from backend
			const profiles = await api.profiles.list({ type: 'community' })

			// Convert profiles to CommunityRef format and update atom
			setCommunities((prev) => {
				// Create a map of existing communities for quick lookup
				const existingMap = new Map(prev.map((c) => [c.idTag, c]))

				// Only sync connected communities
				const connectedProfiles = profiles.filter((p) => p.connected === true)

				// Update or add communities
				const updated: CommunityRef[] = connectedProfiles.map((profile) => {
					const existing = existingMap.get(profile.idTag)
					return {
						idTag: profile.idTag,
						name: profile.name || profile.idTag, // Fallback to idTag if name is missing
						profilePic: profile.profilePic,
						// Preserve existing metadata if available
						isFavorite: existing?.isFavorite || false,
						// Composition: default true (shown). `hidden_in_home` is
						// NULL/absent for shown communities, true only when opted out.
						showInHome: profile.hiddenInHome !== true,
						unreadCount: existing?.unreadCount || 0,
						lastActivityAt: existing?.lastActivityAt || null,
						// Backend reports connected === true here, so the community is no longer
						// pending — clear any stale pending flag carried over from creation.
						isPending: false,
						pendingSince: undefined,
						pendingReason: undefined
					}
				})

				return updated
			})

			// Non-member community rows (partners entered under a hat): display refs for pins only
			// The hat is the front entry (last used), a community I'm in; front `''` = last
			// entered as myself, so no badge.
			const hatOf = (hats?: string[]) => {
				const first = hats?.[0]
				const h = first ? profiles.find((p) => p.idTag === first) : undefined
				return h && { idTag: h.idTag, name: h.name, profilePic: h.profilePic }
			}
			const fresh = profiles
				.filter((p) => p.connected !== true)
				.map((profile) => ({
					idTag: profile.idTag,
					name: profile.name || profile.idTag,
					profilePic: profile.profilePic,
					hat: hatOf(profile.hats),
					isFavorite: false,
					showInHome: true,
					unreadCount: 0,
					lastActivityAt: null
				}))
			// Hatted rows the server list lacks survive only while that hat is still worn,
			// so a stale row never counts as consent in `isKnownContext`.
			store.set(partnerCommunitiesAtom, (prev) => {
				const listed = new Set(profiles.map((p) => p.idTag))
				return [
					...fresh,
					...prev.filter(
						(c) =>
							c.hat &&
							!listed.has(c.idTag) &&
							hattedConsent(store, c.idTag, c.hat.idTag)
					)
				]
			})

			// Self-heal abandoned community mirrors: a community whose picture was
			// uploaded after creation can keep a frozen, empty mirror row that the
			// periodic refresh batch has given up on (see backend
			// `list_stale_profiles` abandonment cutoff). For connected community
			// rows still missing a `profilePic`, fire a best-effort forced refresh
			// on the home-server (this `api`) — once per session per row — and
			// patch the recovered picture into the atom.
			for (const profile of profiles) {
				if (profile.connected !== true || profile.profilePic) continue
				if (refreshAttemptedIdTags.has(profile.idTag)) continue
				refreshAttemptedIdTags.add(profile.idTag)
				api.profiles
					.refresh(profile.idTag)
					.then((p) => {
						if (p?.profilePic) {
							setCommunities((prev) =>
								prev.map((c) =>
									c.idTag === profile.idTag
										? { ...c, profilePic: p.profilePic }
										: c
								)
							)
						}
					})
					.catch((err) => console.warn('community mirror refresh-on-view failed', err))
			}
		} catch (err) {
			setError(err as Error)
			console.error('Failed to load communities:', err)
		} finally {
			setIsLoading(false)
		}
	}, [api, setCommunities])

	/**
	 * Refresh communities list from backend (alias for loadCommunities)
	 */
	const refresh = loadCommunities

	/**
	 * Toggle whether a community's posts appear in the merged home feed.
	 * Optimistically updates the atom, then persists via the profile PATCH
	 * endpoint (rolling back on failure).
	 */
	const setShowInHome = React.useCallback(
		(idTag: string, value: boolean) => {
			setCommunities((prev) =>
				prev.map((c) => (c.idTag === idTag ? { ...c, showInHome: value } : c))
			)
			// shown→hidden: the community gets its own sidebar dot now. Seed its local
			// watermark forward-only from the Home watermark so the dot only flags
			// posts newer than what Home already surfaced (persisted, survives reload).
			if (value === false && auth?.idTag) {
				seedCommunityFromHome(store, api, idTag, auth.idTag)
			}
			api?.profiles.setShowInHome(idTag, value).catch((err) => {
				console.error('Failed to update Show in Home', err)
				setCommunities((prev) =>
					prev.map((c) => (c.idTag === idTag ? { ...c, showInHome: !value } : c))
				)
			})
		},
		[api, setCommunities, store, auth?.idTag]
	)

	/**
	 * Toggle pinned status of a community (with backend sync)
	 */
	// The three writers below read the current pins straight from the store rather than from a
	// `setFavorites` updater: React may re-invoke an updater (StrictMode does in dev), which would
	// fire savePinnedCommunities twice. `store.get` also avoids the stale-closure risk of reading
	// the `favorites` binding.
	/** A pinned partner with no row yet keeps the active context's ref, so it outlives the switch. */
	const rememberPartner = React.useCallback(
		(keys: string[]) => {
			const active = store.get(activeContextAtom)
			if (!active || !pinKeyFor(active.idTag, keys)) return
			if (store.get(communitiesAtom).some((c) => c.idTag === active.idTag)) return
			store.set(partnerCommunitiesAtom, (prev) => [
				...prev.filter((c) => c.idTag !== active.idTag),
				{ ...refFromActive(active), hat: active.hat }
			])
		},
		[store]
	)

	const toggleFavorite = React.useCallback(
		(key: string) => {
			const prev = store.get(favoritesAtom)
			const pinning = !prev.includes(key)
			const newFavorites = pinning ? [...prev, key] : prev.filter((k) => k !== key)
			if (pinning) rememberPartner([key])
			else pruneContextColors([key])
			setFavorites(newFavorites)
			// Save to backend (async, non-blocking)
			savePinnedCommunities(newFavorites)
		},
		[store, setFavorites, savePinnedCommunities, rememberPartner, pruneContextColors]
	)

	/** Drop every pin (bare and hatted) of community `idTag` */
	const unpinCommunity = React.useCallback(
		(idTag: string) => {
			const prev = store.get(favoritesAtom)
			const next = withoutPins(prev, idTag)
			if (next.length === prev.length) return
			setFavorites(next)
			savePinnedCommunities(next)
			pruneContextColors(prev.filter((k) => !next.includes(k)))
		},
		[store, setFavorites, savePinnedCommunities, pruneContextColors]
	)

	/** Pin `idTag` bare, or drop every pin of it when it has any */
	const togglePinCommunity = React.useCallback(
		(idTag: string) => {
			if (pinKeyFor(idTag, store.get(favoritesAtom))) unpinCommunity(idTag)
			else toggleFavorite(idTag)
		},
		[store, unpinCommunity, toggleFavorite]
	)

	/**
	 * Pin communities, appending the not-yet-pinned ones (one backend write for the batch)
	 */
	const pinCommunities = React.useCallback(
		(keys: string[]) => {
			const prev = store.get(favoritesAtom)
			const added = keys.filter((k) => k && !prev.includes(k))
			if (!added.length) return
			const newFavorites = [...prev, ...new Set(added)]
			rememberPartner(added)
			setFavorites(newFavorites)
			savePinnedCommunities(newFavorites)
		},
		[store, setFavorites, savePinnedCommunities, rememberPartner]
	)

	/**
	 * Pin context `key` at `index` in the pinned strip — what a drag from the community
	 * popup onto a chosen slot does. Filtering first makes the same call correct for
	 * both "pin a new one" and "move one that is already pinned".
	 */
	const pinCommunityAt = React.useCallback(
		(key: string, index: number) => {
			const prev = store.get(favoritesAtom)
			const without = prev.filter((k) => k !== key)
			const at = Math.max(0, Math.min(index, without.length))
			const next = [...without.slice(0, at), key, ...without.slice(at)]
			rememberPartner([key])
			setFavorites(next)
			savePinnedCommunities(next)
		},
		[store, setFavorites, savePinnedCommunities, rememberPartner]
	)

	/**
	 * Add a community to the list
	 */
	const addCommunity = React.useCallback(
		(community: CommunityRef) => {
			setCommunities((prev) => {
				// Don't add duplicates
				if (prev.some((c) => c.idTag === community.idTag)) {
					return prev
				}
				return [...prev, community]
			})
		},
		[setCommunities]
	)

	/**
	 * Remove a community from the list
	 */
	const removeCommunity = React.useCallback(
		(idTag: string) => {
			setCommunities((prev) => prev.filter((c) => c.idTag !== idTag))
			// Every pinned variant (bare and hatted) goes with it, and so do pins worn under it.
			const prev = store.get(favoritesAtom)
			const next = withoutPins(prev, idTag, true)
			setFavorites(next)
			pruneContextColors(prev.filter((k) => !next.includes(k)))
		},
		[setCommunities, setFavorites, store, pruneContextColors]
	)

	/**
	 * Add a newly created community (potentially pending DNS propagation)
	 */
	const addPendingCommunity = React.useCallback(
		(
			community: Omit<
				CommunityRef,
				'unreadCount' | 'lastActivityAt' | 'isFavorite' | 'showInHome'
			>
		) => {
			const newCommunity: CommunityRef = {
				...community,
				isFavorite: false,
				showInHome: true,
				unreadCount: 0,
				lastActivityAt: new Date(),
				pendingSince: community.isPending ? new Date() : undefined,
				pendingReason: community.pendingReason
			}

			setCommunities((prev) => {
				// Update existing or add new
				if (prev.some((c) => c.idTag === community.idTag)) {
					return prev.map((c) => (c.idTag === community.idTag ? newCommunity : c))
				}
				return [...prev, newCommunity]
			})
		},
		[setCommunities]
	)

	const pinnedCommunities = React.useMemo(
		() =>
			favoriteCommunities.filter((c, i, a) => a.findIndex((x) => x.idTag === c.idTag) === i),
		[favoriteCommunities]
	)

	return {
		communities,
		/** One PinnedCommunity per pin key, in strip order */
		pinnedEntries: favoriteCommunities,
		/** The raw pinned context keys (`B` or `B|A`) */
		pinnedKeys: favorites,
		/** One entry per community (a community pinned under several hats appears once) */
		pinnedCommunities,
		recent: recentCommunities,
		totalUnread,
		isLoading,
		error,
		refresh,
		loadCommunities,
		toggleFavorite,
		unpinCommunity,
		togglePinCommunity,
		pinCommunities,
		pinCommunityAt,
		setContextColor,
		setShowInHome,
		addCommunity,
		removeCommunity,
		addPendingCommunity
	}
}

/**
 * Hook for managing sidebar state
 *
 * @example
 * ```typescript
 * const { isOpen, toggle, pin } = useSidebar()
 * ```
 */
export function useSidebar() {
	const [sidebar, setSidebar] = useAtom(sidebarAtom)
	const [isOpen, setIsOpen] = useAtom(sidebarOpenAtom)

	const open = React.useCallback(() => setIsOpen(true), [setIsOpen])
	const close = React.useCallback(() => setIsOpen(false), [setIsOpen])
	const toggle = React.useCallback(() => setIsOpen((prev) => !prev), [setIsOpen])

	const pin = React.useCallback(() => {
		setSidebar((prev) => ({ ...prev, isPinned: true }))
	}, [setSidebar])

	const unpin = React.useCallback(() => {
		setSidebar((prev) => ({ ...prev, isPinned: false }))
	}, [setSidebar])

	return {
		isOpen,
		isPinned: sidebar.isPinned,
		open,
		close,
		toggle,
		pin,
		unpin
	}
}

/**
 * Hook for context switching with navigation
 *
 * High-level helper that handles context switching and navigation together.
 *
 * @example
 * ```typescript
 * const { switchTo, isSwitching } = useContextSwitch()
 *
 * // Switch to community and land on its feed
 * await switchTo('alice.community')
 * ```
 */
export function useContextSwitch() {
	const [isSwitching, setIsSwitching] = useAtom(contextSwitchingAtom)
	const [lastSwitch, setLastSwitch] = useAtom(lastContextSwitchAtom)
	const [_recentContexts, setRecentContexts] = useAtom(recentContextsAtom)
	const [activeContext] = useAtom(activeContextAtom)
	const [apiState] = useAtom(apiAtom)
	const navigate = useNavigate()

	// URL is the only source of truth for the active context. switchTo
	// navigates and updates the LRU bookkeeping; the route effect in
	// ctx.tsx observes the new URL and drives the actual setActiveContext
	// call (and clears contextSwitchingAtom when it resolves).
	const switchTo = React.useCallback(
		// `path`, when given, is the whole destination — the sidebar hands over a
		// fully-scoped route so the tail of the current page survives the switch.
		async (idTag: string, path?: string) => {
			const destination = path ?? feedPath(ctxBase(idTag, apiState.idTag))

			if (activeContext?.idTag === idTag) {
				navigate(destination)
				return
			}

			setIsSwitching(true)
			const fromContext = activeContext?.idTag || 'none'

			navigate(destination)

			// Update recent contexts (LRU)
			setRecentContexts((prev) => {
				const filtered = prev.filter((id) => id !== idTag)
				const updated = [idTag, ...filtered].slice(0, 10)
				return updated
			})

			const switchEvent: ContextSwitchEvent = {
				from: fromContext,
				to: idTag,
				timestamp: new Date()
			}
			setLastSwitch(switchEvent)
		},
		[activeContext, apiState.idTag, setRecentContexts, setLastSwitch, setIsSwitching, navigate]
	)

	return {
		switchTo,
		isSwitching,
		lastSwitch
	}
}

/**
 * Switch context and stay in the same kind of place — the behaviour behind every context
 * chip, shared by the top-bar strip and the mobile community sheet.
 *
 * The section rides along minus any object the old context owns (`switchPath`); the route
 * tree decides whether it resolves in the new context. Falls back to the default feed when
 * the current URL is not a context route at all, or when the target context's IDP is
 * disabled (or unknown — we have no token to ask the foreign server yet).
 */
export function useContextSwitchNav(): (idTag: string) => void {
	const { t } = useTranslation()
	const [auth] = useAuth()
	const contextIdpEnabled = useAtomValue(contextIdpEnabledAtom)
	const { switchTo } = useContextSwitch()
	const { close } = useSidebar()
	const { error: toastError } = useToast()
	const location = useLocation()
	const isDesktop = useIsDesktop()
	// Segment 2 of a context route, `undefined` on anything else (`/login`, `/s/:refId`,
	// a bare `/~`) — the pattern matches any two segments, so the sigil test is what rules
	// those out. Only two sections need naming below; `switchPath` decides the rest.
	const sectionMatch = useMatch(CTX_SECTION_MATCH)
	const section = isContextSegment(sectionMatch?.params.contextIdTag)
		? sectionMatch?.params.section
		: undefined

	return React.useCallback(
		(idTag: string) => {
			const base = ctxBase(idTag, auth?.idTag)
			const defaultDestination = feedPath(base)

			let destination = defaultDestination
			if (section) {
				// IDP is per-tenant; fall back to feed when the target context's IDP
				// is disabled, not yet loaded (missing entry) or 'unknown' (a transient
				// lookup failure). `!== true` covers all three on purpose — see
				// contextIdpEnabledAtom. Unlike IdpGuard, nothing is ejected here.
				if (section === 'idp' && contextIdpEnabled[idTag] !== true) {
					destination = defaultDestination
				} else {
					// `/search` keeps its query string: `q`/`type` are the search
					// itself, and dropping them would land on an empty page. No other
					// section carries state that survives a context switch — an app's
					// launch params name resources in the old context.
					const query = section === 'search' ? location.search : ''
					// The raw pathname, not the match's splat param: the splat is
					// decoded, so a `%`-escaped resId would come back raw.
					destination =
						switchPath(location.pathname, idTag, auth?.idTag, (id) =>
							crossContextApps.has(id)
						) + query
				}
			}

			switchTo(idTag, destination).catch((err) => {
				console.error('Failed to switch context:', err)
				toastError(t('Failed to switch context. Please try again.'))
			})

			// On mobile `isOpen` is the community sheet; close it after the user
			// picks a context so it doesn't cover the destination view.
			if (!isDesktop) {
				close()
			}
		},
		[
			switchTo,
			section,
			location.pathname,
			location.search,
			toastError,
			t,
			auth?.idTag,
			contextIdpEnabled,
			isDesktop,
			close
		]
	)
}

/**
 * The real idTag of the **active** context — for API calls, owner halves of a `resId`
 * and cache keys.
 *
 * Derived from `activeContextAtom`, which trails the URL on purpose: `setActiveContext` has
 * to mint a proxy token before the new context is usable, and `useContextAwareApi()` keys
 * its client on the same atom. A URL-derived idTag would name the new community in a cache
 * key while the client still points at home. Use `useCtx()` for building URLs.
 */
export function useCurrentContextIdTag(): string | undefined {
	const [activeContext] = useAtom(activeContextAtom)
	const [auth] = useAuth()
	const [apiState] = useAtom(apiAtom)

	return activeContext?.idTag || auth?.idTag || apiState.idTag
}

/** The active context's display name, else its idTag. */
export function useContextName(): string {
	const name = useAtomValue(activeContextDisplayAtom)?.name
	const idTag = useCurrentContextIdTag()
	return name || idTag || ''
}

/** The active context when it is a community, else `undefined` (home). */
export function useActiveCommunity() {
	const ctx = useAtomValue(activeContextAtom)
	return ctx?.type === 'community' ? ctx : undefined
}

// vim: ts=4
